import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, HelpCircle } from 'lucide-react';

export default function ShelflineSupport() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Support</h1>
            <p className="text-zinc-400 text-lg">Shelfline - Board Game Collection & Play Tracker</p>
          </div>

          {/* Contact Card */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-8 mb-10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-zinc-800 rounded-lg">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Contact Us</h2>
                <p className="text-zinc-400 mb-4">
                  Have a question, feedback, or need help with Shelfline? We're here to help!
                </p>
                <a
                  href="mailto:parkerdevwork@gmail.com?subject=Shelfline Support"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  parkerdevwork@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How does barcode scanning work?</h3>
                <p className="text-zinc-400">
                  Point the camera at the barcode on the bottom or back of a game box. Shelfline
                  checks its built-in catalog first, then public product databases, to identify
                  the game. If a barcode isn't recognized, just search the game by name — the app
                  remembers the match, so scanning that box again is instant. No photos are ever
                  recorded or uploaded.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Do I need to create an account?</h3>
                <p className="text-zinc-400">
                  No! Shelfline works without any account or sign-up. Your collection, plays, and
                  players are stored on your device, and the app works completely offline.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How does "What should we play?" pick games?</h3>
                <p className="text-zinc-400">
                  Tell it how many players are at the table, how much time you have, and the mood.
                  It deals three picks from the games you actually own, weighing player count fit,
                  playtime, your ratings, and your group's play history — best match on top.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Can I back up or move my data?</h3>
                <p className="text-zinc-400">
                  Yes. Settings → Your data lets you export everything (collection, players, plays)
                  as a single JSON file, and import it again on any device. Because your data lives
                  only on your device, we recommend exporting a backup now and then — deleting the
                  app deletes its data.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">A game is missing from the catalog. What do I do?</h3>
                <p className="text-zinc-400">
                  Use the Manual tab on the Add screen to enter it yourself — title, player count,
                  playtime, weight, and tags. It gets a generated cover and works everywhere in the
                  app, including the picker and stats. You can also email us the game name and
                  we'll consider adding it to the catalog.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Is my data safe?</h3>
                <p className="text-zinc-400">
                  Yes. We don't collect any personal information and there are no accounts. Your
                  data stays on your device. For more details, please read our{' '}
                  <Link
                    to="/shelfline/privacy-policy"
                    className="text-emerald-400 hover:text-emerald-300 underline"
                  >
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Shelfline. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
